import { useEffect, useContext } from 'react';
import ApiService from '../../../../services/ApiService';
import { FeedbackContext } from '../../../../contexts/FeedbackContext';
import { InvoiceItem } from './types';

interface Params {
  items: InvoiceItem[];
  setItems: React.Dispatch<React.SetStateAction<InvoiceItem[]>>;
  cfops: { id: number; code: string; description: string }[];
  setCfopsPerItem: React.Dispatch<React.SetStateAction<Map<number, { id: number; code: string; description: string }[]>>>;
  destStateId: number | null;
  isEmitida: boolean;
  companyEmissorId: number | null;
}

export function useItemTaxResolution({
  items,
  setItems,
  cfops,
  setCfopsPerItem,
  destStateId,
  isEmitida,
  companyEmissorId,
}: Params) {
  const { setFeedback } = useContext(FeedbackContext);

  // Backfill do cfopId a partir do código (string) quando a lista de CFOPs carrega
  useEffect(() => {
    if (cfops.length === 0) return;
    setItems((prev) =>
      prev.map((p) => {
        if (p.cfopId || !p.cfop) return p;
        const match = cfops.find((c) => c.code.toString() === String(p.cfop));
        return match ? { ...p, cfopId: match.id } : p;
      })
    );
  }, [cfops, setItems]);

  // CFOPs filtrados por NCM + UF + emissor; auto-seleciona quando há resultado único.
  // Sem emissor selecionado não busca — o cenário depende do emissor (ver aviso no form).
  useEffect(() => {
    if (!destStateId || !companyEmissorId) return;
    const itemsWithNcm = items.filter((item) => item.ncmId);
    if (itemsWithNcm.length === 0) return;

    itemsWithNcm.forEach(async (item) => {
      try {
        const response = await ApiService.fiscal.getCfops({
          ncmId: item.ncmId ?? undefined,
          stateId: destStateId,
          companyEmissorId,
        });
        const data = Array.isArray(response.data) ? response.data : response.data?.data;
        // Entrada sempre gravada (mesmo vazia): o picker distingue "sem cenário" de "ainda não buscou".
        const lista = data || [];
        setCfopsPerItem((prev) => new Map(prev).set(item.id, lista));
        if (lista.length === 1 && !item.cfopId) {
          const cfop = lista[0];
          setItems((prev) =>
            prev.map((p) =>
              p.id === item.id ? { ...p, cfopId: cfop.id, cfop: String(cfop.code) } : p
            )
          );
        }
      } catch {
        // silencioso — fallback para lista global
      }
    });
  }, [destStateId, companyEmissorId, items.map((i) => i.ncmId).join(',')]);

  // Lookup fiscal completo (CST, alíquotas) quando ncmId + cfopId + destStateId + emissor
  // definidos. Agrupado por chave tributária (T12): variantes do mesmo produto (mesmo
  // ncmId+cfopId+destStateId+companyEmissorId) resolvem para o mesmo cenário — um lookup
  // por chave, não um por linha, mesmo com dezenas de variantes na nota.
  useEffect(() => {
    if (!destStateId || isEmitida || !companyEmissorId) return;

    const pendentesPorHash = new Map<string, InvoiceItem[]>();
    for (const item of items) {
      if (!item.ncmId || !item.cfopId) continue;
      const currentHash = `${item.ncmId}-${item.cfopId}-${destStateId}-${companyEmissorId}`;
      if (item._lastLookupHash === currentHash) continue;
      const grupo = pendentesPorHash.get(currentHash) ?? [];
      grupo.push(item);
      pendentesPorHash.set(currentHash, grupo);
    }

    pendentesPorHash.forEach(async (grupo, currentHash) => {
      const [{ ncmId, cfopId }] = grupo;
      const idsDoGrupo = new Set(grupo.map((g) => g.id));

      try {
        const res = await ApiService.fiscal.lookup({
          ncmId: ncmId!,
          cfopId: cfopId!,
          stateId: destStateId,
          companyEmissorId,
        });
        // Contrato substitutivo (D4): sem taxConfig/taxScenario crus — o front só consome
        // o bloco impostos já resolvido pelo backend.
        const { natureza, cenario, impostos } = res.data;

        setItems((prev) =>
          prev.map((p) => {
            if (!idsDoGrupo.has(p.id)) return p;

            const parseBrl = (val: string) =>
              Number(val?.replace(/\./g, '')?.replace(',', '.') || 0);
            const fmtBrl = (val: number) =>
              val.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

            const aliqICMS = Number(impostos.icms.aliquota) || 0;
            const aliqIPI = Number(impostos.ipi.aliquota) || 0;
            const bcIcmsNum = parseBrl(p.bcIcms || p.vlTotal);
            const vlTotalNum = parseBrl(p.vlTotal);

            return {
              ...p,
              cst: impostos.icms.cst,
              cfop: natureza.cfop || p.cfop,
              aliqIcms: aliqICMS,
              percBaseIcms: impostos.icms.percentualBase != null ? Number(impostos.icms.percentualBase) : null,
              aliqIpi: aliqIPI,
              vlIcms: fmtBrl((bcIcmsNum * aliqICMS) / 100),
              vlIpi: fmtBrl((vlTotalNum * aliqIPI) / 100),
              cstPisCofins: impostos.pis.cst || p.cstPisCofins,
              cstIbsCbs: impostos.ibs.cst || p.cstIbsCbs,
              classificacao: impostos.ibs.classificacaoTributaria || p.classificacao,
              vlIbs: fmtBrl((vlTotalNum * (Number(impostos.ibs.aliquota) || 0)) / 100),
              vlCbs: fmtBrl((vlTotalNum * (Number(impostos.cbs.aliquota) || 0)) / 100),
              origemIpi: impostos.ipi.origem,
              // Cenário resolvido mesmo quando o CST veio vazio — a grade usa isso para
              // nomear qual cenário/natureza precisa ser completado, em vez do "000".
              cenarioId: cenario.id ?? null,
              cenarioNatureza: natureza.nome ?? null,
              cenarioAusente: false,
              _lastLookupHash: currentHash,
            };
          })
        );
      } catch (err: any) {
        // 404 (nenhum cenário casou) já aparece na própria linha; toast só para os outros erros.
        const semCenario = err?.response?.status === 404;
        if (!semCenario) {
          setFeedback({
            type: 'error',
            message: 'Erro ao resolver o cenário fiscal desta combinação.',
            isOpen: true,
            statusOk: false,
          });
        }
        // Limpa o preview — valores da combinação anterior não valem para a nova seleção.
        setItems((prev) =>
          prev.map((p) =>
            idsDoGrupo.has(p.id)
              ? {
                  ...p,
                  cst: '',
                  vlIcms: '',
                  vlIpi: '',
                  cstPisCofins: '',
                  cstIbsCbs: '',
                  classificacao: '',
                  vlIbs: '',
                  vlCbs: '',
                  origemIpi: '',
                  cenarioId: null,
                  cenarioNatureza: null,
                  cenarioAusente: semCenario,
                  _lastLookupHash: currentHash,
                }
              : p
          )
        );
      }
    });
  }, [items, destStateId, isEmitida, companyEmissorId]);
}
